import React, { memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowReset20Regular,
    Dismiss20Regular,
    FullScreenMaximize16Regular,
    ZoomIn16Regular,
    ZoomOut16Regular,
} from '@fluentui/react-icons';
import { isMermaidErrorSvg, prepareMermaidSvgForViewer, renderMermaidDiagram } from './mermaidLoader';
import styles from './richMarkdown.module.css';

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

export { isMermaidErrorSvg, prepareMermaidSvgForViewer } from './mermaidLoader';

function clampZoom(value) {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
}

function MermaidFullscreenViewer({ svg, onClose }) {
    const preparedSvg = prepareMermaidSvgForViewer(svg);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [fitZoom, setFitZoom] = useState(1);
    const dragRef = useRef(null);
    const stageRef = useRef(null);
    const contentRef = useRef(null);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = event => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            } else if (event.key === '+' || event.key === '=') {
                event.preventDefault();
                setZoom(prev => clampZoom(prev + ZOOM_STEP));
            } else if (event.key === '-' || event.key === '_') {
                event.preventDefault();
                setZoom(prev => clampZoom(prev - ZOOM_STEP));
            } else if (event.key === '0') {
                event.preventDefault();
                setZoom(fitZoom);
                setOffset({ x: 0, y: 0 });
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [fitZoom, onClose]);

    useLayoutEffect(() => {
        const svgEl = contentRef.current?.querySelector('svg');
        const stage = stageRef.current;
        if (!svgEl || !stage) return;

        const width = Math.max(
            svgEl.clientWidth,
            Number(svgEl.getAttribute('width')) || 0,
            svgEl.getBoundingClientRect().width / Math.max(zoom, 0.01)
        );
        const height = Math.max(
            svgEl.clientHeight,
            Number(svgEl.getAttribute('height')) || 0,
            svgEl.getBoundingClientRect().height / Math.max(zoom, 0.01)
        );
        if (!(width > 0 && height > 0)) return;

        const fit = Math.min(
            1,
            (stage.clientWidth - 80) / (width + 48),
            (stage.clientHeight - 80) / (height + 48)
        );
        const nextFit = fit > 0 && fit < 0.99
            ? clampZoom(Math.max(ZOOM_MIN, Math.floor(fit / ZOOM_STEP) * ZOOM_STEP || fit))
            : 1;
        setFitZoom(nextFit);
        setZoom(nextFit);
        setOffset({ x: 0, y: 0 });
        // Only run when the diagram source changes — not on every zoom.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preparedSvg]);

    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return undefined;
        const onWheel = event => {
            if (!(event.ctrlKey || event.metaKey)) return;
            event.preventDefault();
            const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
            setZoom(prev => clampZoom(prev + delta));
        };
        stage.addEventListener('wheel', onWheel, { passive: false });
        return () => stage.removeEventListener('wheel', onWheel);
    }, []);

    const zoomIn = useCallback(() => setZoom(prev => clampZoom(prev + ZOOM_STEP)), []);
    const zoomOut = useCallback(() => setZoom(prev => clampZoom(prev - ZOOM_STEP)), []);
    const resetView = useCallback(() => {
        setZoom(fitZoom);
        setOffset({ x: 0, y: 0 });
    }, [fitZoom]);

    const onPointerDown = useCallback(event => {
        if (event.button !== 0) return;
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            originX: offset.x,
            originY: offset.y,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }, [offset.x, offset.y]);

    const onPointerMove = useCallback(event => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        setOffset({
            x: drag.originX + (event.clientX - drag.startX),
            y: drag.originY + (event.clientY - drag.startY),
        });
    }, []);

    const endDrag = useCallback(event => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    }, []);

    return createPortal(
        <div
            className={styles.mermaidOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="Mermaid diagram fullscreen viewer">
            <div className={styles.mermaidOverlayToolbar}>
                <span className={styles.mermaidOverlayTitle}>Diagram</span>
                <div className={styles.mermaidOverlayActions}>
                    <button type="button" className={styles.mermaidOverlayBtn} onClick={zoomOut} aria-label="Zoom out">
                        <ZoomOut16Regular />
                    </button>
                    <span className={styles.mermaidZoomLabel}>{Math.round(zoom * 100)}%</span>
                    <button type="button" className={styles.mermaidOverlayBtn} onClick={zoomIn} aria-label="Zoom in">
                        <ZoomIn16Regular />
                    </button>
                    <button type="button" className={styles.mermaidOverlayBtn} onClick={resetView} aria-label="Reset view">
                        <ArrowReset20Regular />
                        <span>Reset</span>
                    </button>
                    <button type="button" className={styles.mermaidOverlayBtn} onClick={onClose} aria-label="Close fullscreen diagram">
                        <Dismiss20Regular />
                        <span>Close</span>
                    </button>
                </div>
            </div>
            <div
                ref={stageRef}
                className={styles.mermaidOverlayStage}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                role="presentation">
                <div
                    ref={contentRef}
                    className={styles.mermaidOverlayCanvas}
                    style={{
                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                    }}
                    dangerouslySetInnerHTML={{ __html: preparedSvg }}
                />
            </div>
            <p className={styles.mermaidOverlayHint}>Drag to pan · ⌘/Ctrl + scroll to zoom · Esc to close</p>
        </div>,
        document.body
    );
}

function MermaidBlock({ code, complete = true }) {
    const reactId = useId();
    const [svg, setSvg] = useState('');
    const [failed, setFailed] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);

    useEffect(() => {
        if (!complete) {
            setSvg('');
            setFailed(false);
            return undefined;
        }

        let cancelled = false;
        const source = String(code || '').trim();
        if (!source) return undefined;

        (async () => {
            try {
                const renderId = `mermaid-${reactId.replace(/:/g, '')}-${Date.now()}`;
                const { svg: rendered } = await renderMermaidDiagram(renderId, source);
                if (cancelled) return;
                if (!rendered || isMermaidErrorSvg(rendered)) {
                    setSvg('');
                    setFailed(true);
                    return;
                }
                const prepared = prepareMermaidSvgForViewer(rendered);
                if (isMermaidErrorSvg(prepared)) {
                    setSvg('');
                    setFailed(true);
                    return;
                }
                setSvg(prepared);
                setFailed(false);
            } catch {
                if (!cancelled) {
                    setSvg('');
                    setFailed(true);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [code, complete, reactId]);

    const openFullscreen = useCallback(() => setFullscreen(true), []);
    const closeFullscreen = useCallback(() => setFullscreen(false), []);

    const source = String(code || '').trim();
    if (!complete) {
        return (
            <pre className={styles.mermaidFallback} aria-label="Mermaid diagram source">
                <code>{source}</code>
            </pre>
        );
    }

    if (failed) {
        return (
            <details className={styles.mermaidFallback}>
                <summary>Diagram could not be displayed</summary>
                <pre aria-label="Mermaid diagram source">
                    <code>{source}</code>
                </pre>
            </details>
        );
    }

    if (!svg) {
        return (
            <div className={styles.mermaidFallback} aria-busy="true" aria-label="Rendering diagram">
                Rendering diagram…
            </div>
        );
    }

    return (
        <>
            <div className={styles.mermaidShell}>
                <div className={styles.mermaidToolbar}>
                    <span className={styles.mermaidToolbarLabel}>Diagram</span>
                    <button
                        type="button"
                        className={styles.mermaidToolbarBtn}
                        onClick={openFullscreen}
                        aria-label="Expand diagram to fullscreen">
                        <FullScreenMaximize16Regular />
                        <span>Expand</span>
                    </button>
                </div>
                <button
                    type="button"
                    className={styles.mermaidPreviewButton}
                    onClick={openFullscreen}
                    aria-label="Mermaid diagram — click to expand">
                    <div
                        className={styles.mermaidBlock}
                        dangerouslySetInnerHTML={{ __html: svg }}
                    />
                </button>
            </div>
            {fullscreen ? <MermaidFullscreenViewer svg={svg} onClose={closeFullscreen} /> : null}
        </>
    );
}

export default memo(MermaidBlock);
